// React imports
import React, { useEffect } from 'react';

// Router imports
import { useNavigate } from 'react-router-dom';

// Redux imports
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { setUserTypes } from '../../redux/userTypesSlice';
import { setDepartments } from '../../redux/departmentsSlice';
import { setSchools } from '../../redux/schoolsSlice';
import { setSupports } from '../../redux/supportSlice';
import { setIPStatusesList } from '../../redux/ipStatusesSlice';

// API imports
import {
  getDepartments,
  getIPStatus,
  getSchools,
  getSupport,
  getUserTypes,
} from '../../api/UserService';

// Type imports
import {
  DepartmentListResponse,
  SchoolListResponse,
  PartnershipListResponse,
  UserRolesResponse,
  IPStatusesListResponse,
} from '../../types/types';

// Constant imports
import { TENANT_ID } from '../../constants/Constants';

// Style imports
import '../../styles/Header.css';

// Component imports
import MobileHeader from './MobileHeader';
import DesktopHeader from './DesktopHeader';

// Custom Hook imports
import useScreenSize from '../../hooks/useScreenSize';

const Header: React.FC<{ onMenuClick?: () => void }> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector((state: RootState) => state.user);
  const isAuthenticated = !!user.userDetails?.id;
  const isMobile = useScreenSize('(max-width:576px)');

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isAuthenticated) {
      const fetchMasterData = async () => {
        await Promise.all([
          getSupportsFromAPI(),
          getUserTypesFromAPI(),
          getDepartmentsFromAPI(),
          getSchoolsFromAPI(),
          getIPStatusesFromAPI(),
        ]);
      };
      fetchMasterData();

      interval = setInterval(
        async () => {
          await fetchMasterData(); // Re-run the fetch on interval
        },
        15 * 60 * 1000, // 15 minutes in milliseconds
      );
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated]);

  const getUserTypesFromAPI = async () => {
    const result = await getUserTypes();
    if ('code' in result) {
      console.error('Error fetching user types:', result);
    } else {
      if ((result as UserRolesResponse).user_types.length > 0) {
        dispatch(setUserTypes((result as UserRolesResponse).user_types));
      } else {
        console.log('user types object is empty');
      }
    }
  };

  const getDepartmentsFromAPI = async () => {
    const result = await getDepartments(TENANT_ID, true);
    if ('code' in result) {
      console.error('Error fetching departments:', result);
    } else {
      if ((result as DepartmentListResponse).departments.length > 0) {
        dispatch(
          setDepartments((result as DepartmentListResponse).departments),
        );
      } else {
        console.log('departments object is empty');
      }
    }
  };

  const getSchoolsFromAPI = async () => {
    const result = await getSchools(TENANT_ID, true);
    if ('code' in result) {
      console.error('Error fetching schools:', result);
    } else {
      if ((result as SchoolListResponse).schools.length > 0) {
        dispatch(setSchools((result as SchoolListResponse).schools));
      } else {
        console.log('schools object is empty');
      }
    }
  };

  const getSupportsFromAPI = async () => {
    const result = await getSupport();
    if ('code' in result) {
      console.error('Error fetching Supports:', result);
    } else {
      if (
        result &&
        (result as PartnershipListResponse).support_or_partnership &&
        (result as PartnershipListResponse).support_or_partnership.length > 0
      ) {
        dispatch(
          setSupports(
            (result as PartnershipListResponse).support_or_partnership,
          ),
        );
      } else {
        console.log('Supports object is empty');
      }
    }
  };
  const getIPStatusesFromAPI = async () => {
    const result = await getIPStatus();
    if ('code' in result) {
      console.error('Error fetching ip statuses:', result);
    } else {
      if (
        result &&
        (result as IPStatusesListResponse).ip_statuses &&
        (result as IPStatusesListResponse).ip_statuses.length > 0
      ) {
        dispatch(
          setIPStatusesList((result as IPStatusesListResponse).ip_statuses),
        );
      } else {
        console.log('ip statuses object is empty');
      }
    }
  };

  return (
    <div className="row justify-content-center g-0">
      {isMobile ? (
        <MobileHeader
          isAuthenticated={isAuthenticated}
          onMenuClick={onMenuClick}
          userId={user.userDetails?.userId}
          navigate={navigate}
        />
      ) : (
        <DesktopHeader isAuthenticated={isAuthenticated} navigate={navigate} />
      )}
    </div>
  );
};

export default Header;
